import { type AttributeValue, type Attributes, type Meter, metrics } from '@opentelemetry/api';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { MeterProvider } from '@opentelemetry/sdk-metrics';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import type { TelemetryConfig } from '../types';

// Instrumentation-scope identity for the library itself. This is distinct from
// the *service* resource (service.name / service.version), which the consuming
// application supplies via TelemetryConfig.
const METER_NAME = '@seeqdev/telemetry';
const METER_VERSION = '0.0.1';

// The SDK is a process-wide singleton held in module scope. We model it with
// free functions + module state (the `@seeqdev/api` axios.utilities.ts
// convention) rather than a class that polices its own single-instance
// lifecycle.
let meterProvider: MeterProvider | null = null;
let meter: Meter | null = null;
let commonAttributes: Attributes = {};

/**
 * Initialize the telemetry SDK from the supplied config: builds the OTel
 * MeterProvider (resource = service.name / service.version / custom attributes)
 * and registers it as the global provider. Idempotent — a second call with the
 * SDK already initialized is a no-op and keeps the existing provider. Callers
 * that want re-init to be an error should go through {@link setupTelemetry}.
 */
export function initTelemetrySdk(config: TelemetryConfig): void {
  if (meterProvider) {
    return;
  }

  meterProvider = new MeterProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: config.serviceName,
      [ATTR_SERVICE_VERSION]: config.serviceVersion,
      ...config.resourceAttributes,
    }),
    readers: config.readers,
  });

  metrics.setGlobalMeterProvider(meterProvider);
  meter = metrics.getMeter(METER_NAME, METER_VERSION);
}

/** Whether {@link initTelemetrySdk} has run and the provider is live. */
export function isTelemetrySdkInitialized(): boolean {
  return meterProvider !== null;
}

/** The library Meter, or `null` before initialization. */
export function getMeter(): Meter | null {
  return meter;
}

/**
 * Set an attribute applied to every metric recording that reads
 * {@link getCommonAttributes} (e.g. the current route). Read lazily at record
 * time so instruments always see the latest value.
 */
export function setCommonAttribute(key: string, value: AttributeValue): void {
  commonAttributes = { ...commonAttributes, [key]: value };
}

export function getCommonAttributes(): Attributes {
  return commonAttributes;
}

/** Flush all readers immediately (e.g. on page hide). No-op before init. */
export async function forceFlush(): Promise<void> {
  await meterProvider?.forceFlush();
}

/** Shut down the provider and clear all module state. */
export async function shutdown(): Promise<void> {
  await meterProvider?.shutdown();
  meterProvider = null;
  meter = null;
  commonAttributes = {};
}

/**
 * Visible for testing only. Synchronously clears module state without shutting
 * the provider down (tests that need a clean teardown should `await shutdown()`
 * first).
 */
export function resetTelemetrySdkForTesting(): void {
  meterProvider = null;
  meter = null;
  commonAttributes = {};
}
